import { formatMinutes } from '@src/common';
import { device } from '@src/common/breakpoints';
import { baseButton, SaleDate } from '@src/components/Account/shared';
import FeedbackMessage from '@src/components/ui/FeedbackMessage';
import { DetailedSaleItem, Order, SaleChange, SaleChangeStatusEnum } from '@src/core/buyerApiClient';
import type { Voucher } from '@src/domains/vouchers';
import { useReservationVouchers, useVoucherDownload } from '@src/features/vouchers';
import { useReservationRescheduling } from '@src/features/sales/reschedule';
import { useAppSelector, useTranslate } from '@src/core/hooks';
import { colors, LayoutColors } from '@src/core/theme';
import {
  BodyExtraSmall,
  BodyRegularMedium,
  BodySmall,
  BodySmallMedium,
  BodySmallSummary,
} from '@src/fonts/typography/Typography';
import React, { useCallback, useMemo, Fragment } from 'react';
import styled from 'styled-components';

import { track } from '@src/services/analytics';

import ReservationTable from './ReservationTable';
import HeaderDownloadButton from './ReservationDownloadButton';

import { appInfoSelector } from '@src/stores/app';
import { Tooltip } from '@src/components/reusable/Tooltip';
import { formatRescheduleAntecedence } from '@src/features/sales/reschedule/utils/formatRescheduleAntecedence';
import { IconCalendarWeek, IconClock } from '@tabler/icons-react';
import { getRescheduleDeadline } from '@src/common/reschedule';
import FeatureFlag from '@src/components/FeatureFlag';
import {
  RescheduleModal as Reschedule,
  RescheduleButton,
  RescheduleSaleChangeStatus,
} from '@src/features/sales/reschedule/legacy';
import {
  getAttributeTypeLabel,
  getLanguageOptionMeta,
  getNonLanguageSelectedAttributes,
  getSelectedLanguageValue,
} from '@src/domains/products';

type ReservationProps = {
  order?: Order;
  saleItem: DetailedSaleItem;
  hasMultipleVouchers: boolean;
  disableReschedule?: boolean;
  isPackageItem?: boolean;
  onSaleChangeCheckout?: (saleChange: SaleChange) => void;
};

const Reservation = (props: ReservationProps) => {
  const {
    order,
    saleItem,
    hasMultipleVouchers,
    disableReschedule = false,
    isPackageItem = false,
    onSaleChangeCheckout,
  } = props;

  const { id, product, rescheduleInfo, scheduleDate, scheduleTimeMinutes } = saleItem;
  const { canReschedule } = rescheduleInfo;
  const appInfo = useAppSelector(appInfoSelector);
  const { i18n } = useTranslate();

  const { vouchers, summary } = useReservationVouchers({
    orderId: order?.id ?? '',
    saleItemId: saleItem.id,
    hasMultipleVouchers,
  });
  const { download, isDownloading } = useVoucherDownload();

  const hasPendingVouchers = summary.hasPending;
  const voucherSplitPolicy = useMemo(() => (vouchers.length === 1 ? 'normal' : 'split'), [vouchers]);

  const {
    state: rescheduling,
    isRescheduleOngoing: reservationRescheduleOngoing,
    availability,
    lastSaleChange,
    showsStatus: shouldShowStatus,
    showsActions: shouldShowReservationActions,
    start: handleReschedulingState,
    cancel: handleCancelReschedule,
    confirm: handleConfirmReschedule,
    dismissApplied: onDismissApplied,
  } = useReservationRescheduling({
    saleItem,
    orderId: order?.id,
    isPackageItem,
    hasMultipleVouchers,
    hasVoucherSplitPolicy: !!voucherSplitPolicy,
    onSaleChangeCheckout,
  });

  const canShowReschedule = availability.isVisible;

  const handleOpenVoucherPdfButtonClick = async (voucher: Voucher) => {
    track('saleViewVoucher', { saleItemId: saleItem.id });

    await download({ kind: 'voucher', id: voucher.id });
  };

  const isVoucherPdfLoading = (voucher: Voucher) => isDownloading(voucher.id);

  const otherSelectedAttributes = useMemo(
    () => getNonLanguageSelectedAttributes(saleItem.selectedAttributes ?? []),
    [saleItem],
  );

  const handleOpenPurchase = useCallback(() => {
    if (!lastSaleChange || !onSaleChangeCheckout || lastSaleChange.status !== SaleChangeStatusEnum.Pending) return;

    onSaleChangeCheckout(lastSaleChange);
  }, [lastSaleChange, onSaleChangeCheckout]);

  const selectedLanguageMeta = useMemo(() => {
    const selectedLanguageValue = getSelectedLanguageValue(saleItem.selectedAttributes ?? []);

    return selectedLanguageValue ? getLanguageOptionMeta(selectedLanguageValue) : null;
  }, [saleItem]);

  if (!vouchers.length) {
    return null;
  }

  return (
    <>
      <ReservationContainer>
        <ReservationHeader>
          <div id='title'>
            <h4>{product.name}</h4>

            {product.appId !== appInfo?.id ? (
              <ProductProvider>{i18n('product_package_adder_sold_by', { owner: product.app.name })}</ProductProvider>
            ) : null}

            <SaleDateContainer>
              <SaleDate
                scheduleDate={scheduleDate}
                scheduleTimeMinutes={scheduleTimeMinutes ? formatMinutes(Number(scheduleTimeMinutes)) : ''}
              />

              {selectedLanguageMeta ? (
                <SelectedAttributeContainer>
                  <span>{i18n('cart_selectedLanguage')}:</span>
                  <span>{selectedLanguageMeta?.label}</span>
                </SelectedAttributeContainer>
              ) : null}

              {otherSelectedAttributes.map((attribute) => (
                <SelectedAttributeContainer key={attribute.type}>
                  <span>{getAttributeTypeLabel(attribute.type, i18n)}:</span>
                  <span>{attribute.value}</span>
                </SelectedAttributeContainer>
              ))}
            </SaleDateContainer>
          </div>

          {shouldShowReservationActions && (
            <Actions>
              {canShowReschedule && lastSaleChange?.status !== SaleChangeStatusEnum.Pending ? (
                <RescheduleButton
                  disabled={disableReschedule}
                  onClick={handleReschedulingState}
                  isPackageItem={isPackageItem}
                />
              ) : null}

              {lastSaleChange?.status === SaleChangeStatusEnum.Pending ? (
                <OpenPurchaseButton onClick={handleOpenPurchase}>
                  <span>{i18n('open_purchase_title')}</span>
                </OpenPurchaseButton>
              ) : null}

              <Tooltip
                arrow
                placement='top'
                showContent={hasPendingVouchers}
                label={i18n('account_sale_download_all_vouchers_tooltip')}
              >
                <span>
                  <HeaderDownloadButton
                    hasMultipleVouchers={hasMultipleVouchers}
                    hasPendingVouchers={hasPendingVouchers}
                    text={i18n('reservation_download')}
                    isVoucherPdfLoading={isVoucherPdfLoading}
                    onOpenVoucherPdfButtonClick={handleOpenVoucherPdfButtonClick}
                    vouchers={vouchers}
                  />
                </span>
              </Tooltip>
            </Actions>
          )}
        </ReservationHeader>

        <FeedbacksWrapper>
          <FeatureFlag
            feature='PR-3691-charged-reschedule'
            fallback={() => (
              <Fragment>
                {rescheduling.isDone && !rescheduling.isError && !reservationRescheduleOngoing ? (
                  <FeedbackMessage message={i18n('account_sale_reschedule_success_message')} type='success' />
                ) : null}
              </Fragment>
            )}
            active={() => {
              if (!lastSaleChange || !shouldShowStatus) return null;
              return (
                <RescheduleSaleChangeStatus saleChange={lastSaleChange} i18n={i18n} onDismiss={onDismissApplied} />
              );
            }}
          />

          {rescheduling.isError && !reservationRescheduleOngoing
            ? rescheduling.errorKeys.map((errorKey, index) => (
                <FeedbackMessage
                  key={index}
                  message={i18n(`reschedule_error_${errorKey as keyof typeof i18n}`, {
                    minAntecedence: formatRescheduleAntecedence(
                      product.reservationReschedulingSettings?.reschedulingAntecedenceMinutes ?? 0,
                      i18n,
                    ),
                  })}
                  type='danger'
                />
              ))
            : null}
        </FeedbacksWrapper>

        {rescheduling.isDone && reservationRescheduleOngoing ? <span>{i18n('reservation_rescheduling')}</span> : null}

        {canReschedule && !rescheduling.isGoing ? (
          <FeatureFlag
            feature='PR-3691-charged-reschedule'
            active={() => (
              <RescheduleInfoContainer>
                <RescheduleInfoItem>
                  <IconCalendarWeek size={20} />
                  <p>
                    {i18n('reschedule_info_remaining_reschedule_count')}
                    <span>{rescheduleInfo.remainingRescheduleCount}</span>
                  </p>
                </RescheduleInfoItem>

                <RescheduleInfoItem>
                  <IconClock size={20} />
                  <p>
                    {i18n('reschedule_info_reschedule_until_title')}
                    <span>{getRescheduleDeadline(rescheduleInfo.rescheduleUntil, i18n)}</span>
                  </p>
                </RescheduleInfoItem>
              </RescheduleInfoContainer>
            )}
          />
        ) : null}

        <ReservationTable
          hasMultipleVouchers={hasMultipleVouchers}
          saleItem={saleItem}
          vouchers={vouchers}
          onOpenVoucherPdf={handleOpenVoucherPdfButtonClick}
          isVoucherPdfLoading={isVoucherPdfLoading}
        />
      </ReservationContainer>

      {canReschedule && rescheduling.isGoing ? (
        <Reschedule
          reservationId={id}
          onCancel={handleCancelReschedule}
          onConfirm={handleConfirmReschedule}
          saleItem={saleItem}
        />
      ) : null}
    </>
  );
};

export default Reservation;

const ReservationContainer = styled.section`
  width: 100%;
  background: ${LayoutColors.whiteLight};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px;
  max-width: 828px;
  border-radius: 8px;
  align-self: stretch;
  flex-grow: 0;

  ${device.mediumTablet} {
    padding: 16px;
  }
`;

const SelectedAttributeContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px !important;
  align-items: flex-start !important;
`;

const SaleDateContainer = styled.div`
  display: flex;
  flex-direction: column !important;
  gap: 8px;
`;

const ReservationHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${LayoutColors.grayMain};

  div#title {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding-bottom: 8px;
    width: 100%;

    h4 {
      ${BodyRegularMedium};
      color: ${LayoutColors.blackMain};
    }

    div {
      display: flex;
      flex-direction: row;
      gap: 8px;

      span {
        ${BodySmallSummary};
        color: ${LayoutColors.blackMain};
      }

      span#voucher_consumed_date {
        font-weight: 400 !important;
      }
    }
  }

  ${device.mediumTablet} {
    flex-direction: row;
    justify-content: space-between;
    align-items: baseline;
    gap: 16px;
    width: 100%;
    border-bottom: none;

    div#title {
      border-bottom: none;
      padding-bottom: 0;

      div {
        span {
          ${BodySmallMedium};
        }
      }
    }
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  gap: 16px;

  ${device.smallTablet} {
    flex-direction: row;
    align-items: center;
    max-width: fit-content;
    gap: 8px;
  }
`;

const ProductProvider = styled.p`
  ${BodyExtraSmall};
  color: ${LayoutColors.blackMain};
  margin-top: -8px;
`;

const FeedbacksWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  gap: 8px;
  margin-bottom: 8px;
`;

export const RescheduleInfoContainer = styled.div`
  display: flex;
  padding: 8px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-radius: 4px;
  background: ${colors.blue[50]};
`;

export const RescheduleInfoItem = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  align-self: stretch;
  color: ${colors.blue[600]};

  svg {
    color: inherit;
  }

  p {
    ${BodySmallMedium};
    font-weight: 600;
    color: inherit;

    span {
      ${BodySmall};
      color: inherit;
    }
  }
`;

const OpenPurchaseButton = styled.button`
  ${baseButton};

  background: transparent;
  transition: all 0.2s linear;
  color: ${LayoutColors.justBlue};
  border: 1px solid ${LayoutColors.justBlue};

  &:hover {
    background: ${LayoutColors.justBlue};
    color: ${LayoutColors.whiteLight};
  }

  ${device.mediumTablet} {
    width: 100%;
  }

  span {
    font-size: inherit;
    font-weight: inherit;
    color: inherit;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: inline-block;
  }
`;
